import {Canvas, useLoader, Vector3} from "@react-three/fiber";
import {OrbitControls, PerspectiveCamera} from "@react-three/drei";
import {STLLoader} from "three/examples/jsm/loaders/STLLoader";
import {OBJLoader} from "three/examples/jsm/loaders/OBJLoader";
import {BufferGeometry, Loader, MathUtils} from "three";
import {CameraPreset} from "./types";
import {$KTM, Action, KeyMetadatas} from "../../../shared/types";
import {Type} from "../../../shared/json/helpers";
import React, {ReactElement, useState} from "react";
import {Button, Card, Tooltip} from "@mui/material";
import {Arch, ArchSelect} from "./ArchSelect";
import {StyledBoxColumn} from "../../../shared/StyledComponents";
import {ReactComponent as Layout1} from "./res/icons/layout_1.svg";
import {ReactComponent as Layout2} from "./res/icons/layout_2.svg";
import {ReactComponent as Layout5} from "./res/icons/layout_5.svg";
import {createIcon} from "../../../shared/icons";
import {MonochromePhotosOutlined, RoundedCornerOutlined} from "@mui/icons-material";

export enum ViewSceneType {
  STL = "stl",
  OBJ = "obj",
}

type ViewSceneTypeMetadata = {
  loader: Type<Loader>,
}

const VIEW_SCENE_TYPE_KTM = new KeyMetadatas<ViewSceneTypeMetadata>([
  $KTM(ViewSceneType.STL, {loader: STLLoader}),
  $KTM(ViewSceneType.OBJ, {loader: OBJLoader}),
]);

export type ViewSceneProps = {
  url: string,
  type: ViewSceneType,
  cameraPreset?: CameraPreset,
}

function getPositionForCameraPreset(cameraPreset: CameraPreset): Vector3 {
  switch (cameraPreset) {
    case CameraPreset.FRONT:
      return [0, 0, 60];
    case CameraPreset.TOP:
      return [0, 60, 0];
    case CameraPreset.LEFT:
      return [-60, 0, 0];
    case CameraPreset.RIGHT:
      return [60, 0, 0];
    case CameraPreset.DEFAULT:
    default:
      return [0, 60, 60];
  }
}

function ViewScene_({url, Loader, cameraPreset}: {
  url: string,
  Loader: Type<Loader>,
  cameraPreset?: CameraPreset
}): ReactElement {
  const geom: BufferGeometry = useLoader(Loader, url);
  // @ts-ignore
  return <Canvas frameloop="demand" style={{position: "absolute", left: 0, top: 0, right: 0, bottom: 0}}>
    <PerspectiveCamera makeDefault fov={1}
                       position={getPositionForCameraPreset(cameraPreset)}
                       matrixWorldAutoUpdate={undefined}
                       getObjectsByProperty={undefined}/>
    <axesHelper/>
    <mesh geometry={geom} rotation-x={MathUtils.degToRad(-90)}>
      <meshPhongMaterial color={"white"} reflectivity={100}/>
      <directionalLight position={[0, 60, 0]}/>
      <directionalLight position={[60, 0, 0]}/>
      <directionalLight position={[0, 0, 60]}/>
      <directionalLight position={[60, 60, 60]}/>
      <directionalLight position={[-60, -60, -60]} intensity={.25}/>
    </mesh>
    <OrbitControls/>
  </Canvas>;
}

function ToolbarButton({action, selected}: { action: Action, selected?: boolean }): ReactElement {
  return <Tooltip title={action.text} placement={"left"}>
    <Card>
      <Button
        variant={selected ? "contained" : null}
        disabled={action.isDisabledFn?.()}
        style={{width: 72, height: 72, minWidth: 0, padding: 0}}
        onClick={(event) => action.onClick?.(event)}>
        <action.iconType style={{width: 36, height: 36}}/>
      </Button>
    </Card>
  </Tooltip>;
}

enum Layout {
  LAYOUT_1 = "layout_1",
  LAYOUT_2 = "layout_2",
  LAYOUT_5 = "layout_5",
}

export function ViewScene(props: ViewSceneProps) {
  const [arch, setArch] = useState<Arch>(Arch.FULL);
  const [layout, setLayout] = useState<Layout>(Layout.LAYOUT_1);
  const Loader = VIEW_SCENE_TYPE_KTM.findMetadata(props.type)?.loader;
  if (!Loader) {
    return null;
  }
  return <>
    <ViewScene_ Loader={Loader} url={props.url} cameraPreset={props.cameraPreset}/>
    <ArchSelect
      arch={arch}
      setArch={setArch}
      style={{position: "absolute", left: 24, top: 24}}/>
    <StyledBoxColumn style={{position: "absolute", right: 24, top: 24}}>
      {<ToolbarButton
        selected={layout === Layout.LAYOUT_1}
        action={new Action("Single Window", () => setLayout(Layout.LAYOUT_1), createIcon(<Layout1/>))}/>}
      {<ToolbarButton
        selected={layout === Layout.LAYOUT_2}
        action={new Action("Dual Window", () => setLayout(Layout.LAYOUT_2), createIcon(<Layout2/>))}/>}
      {<ToolbarButton
        selected={layout === Layout.LAYOUT_5}
        action={new Action("Five Window", () => setLayout(Layout.LAYOUT_5), createIcon(<Layout5/>))}/>}
      {<ToolbarButton
        action={new Action("Toggle Color", () => {}, MonochromePhotosOutlined)}/>}
      {<ToolbarButton
        action={new Action("Toggle Occlusal Clearance", () => {}, RoundedCornerOutlined)}/>}
    </StyledBoxColumn>
  </>;
}
